import { Link, router, usePage } from "@inertiajs/react";
import AppLayout from "@/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { Swords, CalendarDays, Users, Dices, Plus, MapPin, Trophy } from "lucide-react";
import MyLibrarySection from "@/components/app/MyLibrarySection";
import { EventBadge } from "@/components/app/EventBadge";
import EmptyState from "@/components/app/EmptyState";
import { formatDateParts } from "@/lib/timeHelpers";

// ─────────────────────────────────────────────────────────────
// Root
// ─────────────────────────────────────────────────────────────

export default function Home() {
  const { routes, auth, upcoming_events = [], past_events = [], teams = [], library_games = [] } = usePage().props;

  if (auth.authenticated && auth.user?.approved) {
    return (
      <Dashboard
        user={auth.user}
        routes={routes}
        upcomingEvents={upcoming_events}
        pastEvents={past_events}
        teams={teams}
        libraryGames={library_games}
      />
    );
  }

  return <MarketingPage routes={routes} />;
}

// ─────────────────────────────────────────────────────────────
// Dashboard
// ─────────────────────────────────────────────────────────────

function Dashboard({ user, routes, upcomingEvents, pastEvents, teams, libraryGames }) {
  return (
    <AppLayout>
      <div className="container pt-8 pb-0 space-y-8 max-w-7xl">
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              Welcome back, {user.first_name || user.full_name}
            </h1>
            <p className="text-muted-foreground mt-1">Ready to roll some dice?</p>
          </div>
          <Button asChild>
            <Link href={routes.new_event}>
              <Plus className="h-4 w-4 mr-1.5" />
              Create Event
            </Link>
          </Button>
        </div>

        {/* Events + Teams */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <UpcomingEventsCard events={upcomingEvents} routes={routes} />
          <TeamsCard teams={teams} routes={routes} />
        </div>

        {/* Recent past events */}
        {pastEvents.length > 0 && (
          <RecentEventsCard events={pastEvents} />
        )}
      </div>

      {/* BGG Library — full width */}
      <div className="px-4 sm:px-6 py-8">
        <GameLibrarySection user={user} routes={routes} games={libraryGames} />
      </div>

      <div className="container pb-8 max-w-7xl">
        {/* Plays */}
        <PlaysSection routes={routes} />
      </div>
    </AppLayout>
  );
}

// ─────────────────────────────────────────────────────────────
// Upcoming Events
// ─────────────────────────────────────────────────────────────


function UpcomingEventsCard({ events, routes }) {
  return (
    <SectionCard
      icon={<CalendarDays className="h-5 w-5" />}
      title="Upcoming Events"
      count={events.length}
      action={<Link href={routes.events} className="text-xs text-primary hover:underline">Browse all →</Link>}
    >
      {events.length === 0 ? (
        <EmptyState
          icon={<CalendarDays className="h-8 w-8 text-muted-foreground/50" />}
          message="No upcoming events"
          sub="Host one or RSVP to a public event."
        >
          <div className="flex gap-2 mt-3">
            <Button asChild size="sm">
              <Link href={routes.new_event}>Create event</Link>
            </Button>
            <Button asChild variant="outline" size="sm">
              <Link href={routes.events}>Browse</Link>
            </Button>
          </div>
        </EmptyState>
      ) : (
        <ul className="divide-y divide-border">
          {events.map((event) => (
            <li key={event.id}>
              <Link
                href={event.path}
                className="flex items-start gap-3 py-3 hover:bg-muted/50 -mx-5 px-5 transition-colors group"
              >
                <EventDateBadge date={event.starts_at} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate group-hover:text-primary transition-colors">
                    {event.title}
                  </p>
                  {event.location_name && (
                    <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                      <MapPin className="h-3 w-3 shrink-0" />
                      <span className="truncate">{event.location_name}</span>
                    </p>
                  )}
                </div>
                <EventBadge relationship={event.relationship} rsvpStatus={event.rsvp_status} />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </SectionCard>
  );
}

function EventDateBadge({ date }) {
  const { month, day } = formatDateParts(date);
  return (
    <div className="shrink-0 w-10 text-center">
      <p className="text-xs font-medium text-primary uppercase">{month}</p>
      <p className="text-lg font-bold leading-none">{day}</p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Recent Events (past 14 days)
// ─────────────────────────────────────────────────────────────

function RecentEventsCard({ events }) {
  return (
    <SectionCard
      icon={<CalendarDays className="h-5 w-5" />}
      title="Recent Events"
      count={events.length}
    >
      <ul className="divide-y divide-border">
        {events.map((event) => (
          <li key={event.id}>
            <Link
              href={event.path}
              className="flex items-start gap-3 py-3 hover:bg-muted/50 -mx-5 px-5 transition-colors group"
            >
              <EventDateBadge date={event.starts_at} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate group-hover:text-primary transition-colors">
                  {event.title}
                </p>
                {event.location_name && (
                  <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                    <MapPin className="h-3 w-3 shrink-0" />
                    <span className="truncate">{event.location_name}</span>
                  </p>
                )}
              </div>
              <EventBadge relationship={event.relationship} rsvpStatus={event.rsvp_status} />
            </Link>
          </li>
        ))}
      </ul>
    </SectionCard>
  );
}

// ─────────────────────────────────────────────────────────────
// Teams
// ─────────────────────────────────────────────────────────────

function TeamsCard({ teams, routes }) {
  return (
    <SectionCard
      icon={<Users className="h-5 w-5" />}
      title="Your Teams"
      count={teams.length}
      action={<Link href={routes.new_team} className="text-xs text-primary hover:underline">New team →</Link>}
    >
      {teams.length === 0 ? (
        <EmptyState
          icon={<Users className="h-8 w-8 text-muted-foreground/50" />}
          message="No teams yet"
          sub="Create a team to keep your regular crew organised."
        >
          <Button asChild size="sm" className="mt-3">
            <Link href={routes.new_team}>Create team</Link>
          </Button>
        </EmptyState>
      ) : (
        <ul className="divide-y divide-border">
          {teams.map((team) => (
            <li key={team.id}>
              <Link
                href={team.path}
                className="flex items-center gap-3 py-3 hover:bg-muted/50 -mx-5 px-5 transition-colors group"
              >
                <div className="h-9 w-9 rounded-lg bg-accent flex items-center justify-center shrink-0 text-sm font-bold text-accent-foreground">
                  {team.name[0].toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate group-hover:text-primary transition-colors">
                    {team.name}
                  </p>
                  {team.description && (
                    <p className="text-xs text-muted-foreground truncate">{team.description}</p>
                  )}
                </div>
                <span className="shrink-0 text-xs text-muted-foreground">
                  {team.member_count} {team.member_count === 1 ? "member" : "members"}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </SectionCard>
  );
}

// ─────────────────────────────────────────────────────────────
// Game Library
// ─────────────────────────────────────────────────────────────

function GameLibrarySection({ games, routes }) {
  const canTeachIds = new Set(games.filter((g) => g.can_teach).map((g) => g.bgg_id ?? g.bgg_game_id));
  const visitOpts = { preserveScroll: true, preserveState: true };

  function toggleCanTeach(bggId) {
    if (canTeachIds.has(bggId)) {
      router.delete(`${routes.can_teach_library}/${bggId}/can_teach`, visitOpts);
    } else {
      router.post(`${routes.can_teach_library}/${bggId}/can_teach`, {}, visitOpts);
    }
  }

  return (
    <MyLibrarySection
      games={games}
      showTabs={true}
      canTeachIds={canTeachIds}
      myCanTeachIds={canTeachIds}
      onCanTeach={toggleCanTeach}
    />
  );
}

// ─────────────────────────────────────────────────────────────
// Plays
// ─────────────────────────────────────────────────────────────

function PlaysSection({ routes }) {
  const plays = []; // Will be populated from props once play recording is built

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden">
      <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-border">
        <div className="flex items-center gap-2">
          <Dices className="h-5 w-5 text-muted-foreground" />
          <h2 className="font-semibold">Recent Plays</h2>
          {plays.length > 0 && (
            <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
              {plays.length} logged
            </span>
          )}
        </div>
        <Button asChild variant="outline" size="sm" className="gap-1.5 text-xs">
          <Link href={routes.events}>
            <Trophy className="h-3.5 w-3.5" />
            Find an event to play
          </Link>
        </Button>
      </div>

      <div className="p-5">
        {plays.length === 0 ? (
          <EmptyState
            icon={<Dices className="h-8 w-8 text-muted-foreground/50" />}
            message="No plays logged yet"
            sub="Attend an event and log your plays directly from the event page. Your history — and BGG sync — lives here."
          >
            <Button asChild size="sm" variant="outline" className="mt-3">
              <Link href={routes.events}>Browse events</Link>
            </Button>
          </EmptyState>
        ) : (
          <PlaysList plays={plays} />
        )}
      </div>
    </div>
  );
}

function PlaysList({ plays }) {
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b border-border text-xs text-muted-foreground">
          <th className="text-left pb-2 font-medium">Game</th>
          <th className="text-left pb-2 font-medium">Event</th>
          <th className="text-left pb-2 font-medium">Players</th>
          <th className="text-left pb-2 font-medium">Date</th>
          <th className="text-left pb-2 font-medium">BGG</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-border">
        {plays.map((play) => (
          <tr key={play.id} className="hover:bg-muted/40 transition-colors">
            <td className="py-2.5 font-medium">{play.game_title}</td>
            <td className="py-2.5 text-muted-foreground">{play.event_title}</td>
            <td className="py-2.5 text-muted-foreground">{play.num_players}</td>
            <td className="py-2.5 text-muted-foreground">{new Date(play.played_on).toLocaleDateString()}</td>
            <td className="py-2.5">
              {play.bgg_play_id ? (
                <span className="text-green-600 dark:text-green-400 text-xs font-medium">Synced</span>
              ) : (
                <span className="text-muted-foreground text-xs">Pending</span>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ─────────────────────────────────────────────────────────────
// Shared primitives
// ─────────────────────────────────────────────────────────────

function SectionCard({ icon, title, count, action, children }) {
  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden">
      <div className="flex items-center justify-between px-5 py-4 border-b border-border">
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground">{icon}</span>
          <h2 className="font-semibold">{title}</h2>
          {count > 0 && (
            <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">{count}</span>
          )}
        </div>
        {action}
      </div>
      <div className="px-5 py-1">{children}</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Marketing page (guest)
// ─────────────────────────────────────────────────────────────

function MarketingPage({ routes }) {
  return (
    <AppLayout>
      {/* Hero */}
      <section className="section-py border-b border-border">
        <div className="container text-center max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 rounded-full bg-accent text-accent-foreground px-4 py-1.5 text-sm font-medium mb-6">
            <Swords className="h-3.5 w-3.5" />
            Board games &amp; TTRPGs
          </div>
          <h1 className="text-5xl font-bold tracking-tight mb-4">
            Find your next<br />
            <span className="text-primary">game night</span>
          </h1>
          <p className="text-lg text-muted-foreground mb-8 max-w-xl mx-auto">
            GameKnight makes it easy to discover events, organize sessions, and build your tabletop crew.
          </p>
          <div className="flex gap-3 justify-center">
            <Button asChild size="lg">
              <Link href={routes.signup}>Get started free</Link>
            </Button>
            <Button asChild variant="outline" size="lg">
              <Link href={routes.events}>Browse events</Link>
            </Button>
          </div>
        </div>
      </section>

      <FeatureSection
        imageRight
        label="Discover"
        heading="Game nights, right where you are"
        body={[
          "Stop scrolling Discord servers and Facebook groups hoping someone posts a game night. GameKnight puts public tabletop events on one map — board games, TTRPGs, wargames, and everything in between.",
          "Filter by game type, group size, or experience level. RSVP in two clicks. Show up and play.",
        ]}
        img="https://picsum.photos/seed/gk-discover/800/560"
        imgAlt="People gathered around a table playing a board game"
        cta={{ label: "Browse events", href: routes.events }}
      />

      <FeatureSection
        label="Host"
        heading="Running a game night has never been easier"
        body={[
          "Create an event in under a minute — set the date, location, player limit, and whether it's open to the public or invite-only. GameKnight handles RSVPs, the guest list, and keeps everyone in the loop.",
          "Whether you're hosting 4 people for Gloomhaven or 20 for a Magic: The Gathering draft, you've got the tools to pull it off.",
        ]}
        img="https://picsum.photos/seed/gk-host/800/560"
        imgAlt="A person organizing cards and game pieces on a table"
        cta={{ label: "Create an event", href: routes.signup }}
        muted
      />

      <FeatureSection
        imageRight
        label="Teams"
        heading="Your crew, organized"
        body={[
          "Got a regular group? Build a team. Add your friends once, then spin up events for the whole crew in seconds — no more \"who did I forget to text\" moments.",
          "Teams keep your recurring sessions tight: shared history, easy invites, and a home base for your group whether you meet weekly or whenever someone can host.",
        ]}
        img="https://picsum.photos/seed/gk-teams/800/560"
        imgAlt="A group of friends laughing and playing a tabletop game"
        cta={{ label: "Start a team", href: routes.signup }}
      />

      <FeatureSection
        label="BGG"
        heading="Your BoardGameGeek collection, actually useful"
        body={[
          "Link your BGG account and your entire game library syncs to GameKnight. See what games your fellow attendees are bringing. Mark what you want to play. Log plays back to BGG without leaving the app.",
          "No more hauling five games only to find out three people already brought Wingspan. Know before you go.",
        ]}
        img="https://picsum.photos/seed/gk-bgg/800/560"
        imgAlt="A shelf lined with colorful board game boxes"
        cta={{ label: "Get started", href: routes.signup }}
        muted
      />

      <section className="section-py border-t border-border">
        <div className="container text-center max-w-2xl mx-auto">
          <h2 className="text-4xl font-bold tracking-tight mb-4">Ready to roll?</h2>
          <p className="text-muted-foreground text-lg mb-8">
            Join GameKnight free. Find a game night this week.
          </p>
          <Button asChild size="lg">
            <Link href={routes.signup}>Create your account</Link>
          </Button>
        </div>
      </section>
    </AppLayout>
  );
}

function FeatureSection({ heading, label, body, img, imgAlt, cta, imageRight = false, muted = false }) {
  const image = (
    <div className="relative w-full aspect-[4/3] rounded-2xl overflow-hidden shadow-md">
      <img src={img} alt={imgAlt} className="w-full h-full object-cover" />
    </div>
  );
  const text = (
    <div className="flex flex-col justify-center gap-5">
      <span className="text-sm font-semibold uppercase tracking-widest text-primary">{label}</span>
      <h2 className="text-4xl font-bold tracking-tight leading-tight">{heading}</h2>
      <div className="space-y-3">
        {body.map((p, i) => (
          <p key={i} className="text-muted-foreground text-lg leading-relaxed">{p}</p>
        ))}
      </div>
      {cta && (
        <div>
          <Button asChild size="lg">
            <Link href={cta.href}>{cta.label}</Link>
          </Button>
        </div>
      )}
    </div>
  );
  return (
    <section className={`section-py border-b border-border${muted ? " bg-muted/40" : ""}`}>
      <div className="container">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {imageRight ? <>{text}{image}</> : <>{image}{text}</>}
        </div>
      </div>
    </section>
  );
}
